import { HttpException } from "http-repository-cache";
import RBACMatrixRepository from "@/core/administration/repositories/RBACMatrixRepository";
import type {
  ApiRBACResponse,
  ApiRBACRoleItem,
  RBACMatrixDetails,
  RBACMatrixDetailsResponse,
  RBACMatrixFilters,
  RBACMatrixResponse,
} from "@/core/administration/entities/RBACMatrix";
import { HttpClient } from "../../http/HttpClient";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import { executeVoidRequest } from "../../http/services/executeRequest/executeRequest";
import {
  getRBACMatrixDetailsUrl,
  getRBACMatrixUrl,
  publishRoleUrl,
} from "../../api/rbacMatrixBackendEndpoints/rbacMatrixBackendEndpoints";
import RBACMatrixMappers from "@/core/administration/mappers/RBACMatrixMappers";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "core/shared/types/RepositoryResult";

export default class RBACMatrixRepositoryBackend implements RBACMatrixRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getRBACMatrix(
    filters: RBACMatrixFilters,
  ): Promise<RepositoryResult<RBACMatrixResponse>> {
    try {
      const { offset, rows, status } = RBACMatrixMappers.toApiParams(filters);
      const url = getRBACMatrixUrl(offset, rows, status);

      const response = await this.httpClient.get<
        ApiResponse<{ items: ApiRBACRoleItem[]; totalElements: number }>
      >({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        items: ApiRBACRoleItem[];
        totalElements: number;
      }>(response);

      if (result.success && result.data) {
        const rawTotalElements = result.data.totalElements;
        const totalElements =
          typeof rawTotalElements === "number"
            ? rawTotalElements
            : typeof rawTotalElements === "string"
              ? parseInt(rawTotalElements, 10) || 0
              : 0;

        const items = result.data.items || [];

        const apiPayload: ApiRBACResponse = {
          code: response.code,
          message: response.message,
          data: { items, totalElements },
        };

        const page = filters.page ?? 0;
        const mapped = RBACMatrixMappers.fromApiResponse(
          apiPayload,
          page,
          rows,
        );
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(
          error as ApiResponse<unknown> | HttpException<unknown, unknown>,
        ),
      );
    }
  }

  async getRoleDetailsById(
    id: string,
  ): Promise<RepositoryResult<RBACMatrixDetailsResponse>> {
    try {
      const response = await this.httpClient.get<
        ApiResponse<RBACMatrixDetails>
      >({
        url: getRBACMatrixDetailsUrl(id),
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<RBACMatrixDetails>(response);

      if (result.success && result.data) {
        const apiPayload: RBACMatrixDetailsResponse = {
          code: response.code,
          message: response.message,
          data: response.data,
        };

        const mapped = RBACMatrixMappers.fromApiRoleDetails(apiPayload.data);
        return RepositoryResultFactory.success({
          code: apiPayload.code,
          message: apiPayload.message,
          data: mapped,
        });
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(
          error as ApiResponse<unknown> | HttpException<unknown, unknown>,
        ),
      );
    }
  }

  async publishRoleById(id: string): Promise<RepositoryResult<unknown>> {
    return executeVoidRequest(() =>
      this.httpClient.patch<ApiResponse<unknown>, { roleId: string }>({
        url: publishRoleUrl(id),
        // body: { roleId: id },
        withAuth: true,
      }),
    );
  }
}
